import { useMemo, useRef } from 'react';

const sameRows = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * `items` grouped by `keyOf(item)` → Map<key, item[]>, keeping last time's array for a
 * group whose rows are the same objects in the same order. Rows keep their identity
 * until their doc changes (data/rows.js), so a memoized panel fed one group skips
 * re-rendering when only another group changed. `keyOf` must be a stable function.
 */
export function useStableGroups(items, keyOf) {
  const prev = useRef(new Map());
  return useMemo(() => {
    const next = new Map();
    items.forEach(i => {
      const k = keyOf(i);
      if (!next.has(k)) next.set(k, []);
      next.get(k).push(i);
    });
    next.forEach((rows, k) => {
      const old = prev.current.get(k);
      if (old && sameRows(old, rows)) next.set(k, old);
    });
    prev.current = next;
    return next;
  }, [items, keyOf]);
}

const sameSuggestions = (a, b) => a.length === b.length && a.every((x, i) => {
  const y = b[i];
  return x.name === y.name && x.score === y.score && x.count === y.count
    && x.lastDate === y.lastDate && x.fromHistory === y.fromHistory;
});

/**
 * buildCatalogs() output with each list's array kept from last time when its
 * suggestions are unchanged — adding an item to Groceries rebuilds Groceries' picker,
 * not all eight.
 */
export function useStableCatalogs(catalogs) {
  const prev = useRef({});
  return useMemo(() => {
    const out = {};
    Object.keys(catalogs).forEach(id => {
      const old = prev.current[id];
      out[id] = old && sameSuggestions(old, catalogs[id]) ? old : catalogs[id];
    });
    prev.current = out;
    return out;
  }, [catalogs]);
}
